import { debugLog, debugWarn } from './debug';
import { orderCandidateIps, isPrivateHost } from './serverStatus';

/**
 * Build a member NVR's candidate JSON-RPC URLs in client-vantage order (#86bb7phhh).
 *
 * Returns the FULL ordered candidate list (preferred first). This lets the eView login/jump paths GUESS
 * a member's URL synchronously (no per-member probe -> instant login) while keeping every alternate
 * around so a wrong guess self-heals instead of stranding that member on a "failed" tile.
 * See resolveMemberServerUrl.
 *
 * A member is reachable at EITHER its public IP (through NAT, on bPort) or its LAN IP. Crucially the LAN
 * side needs TWO candidates, because `bPort` is the PUBLIC port and says nothing about what the box
 * listens on internally: NAT may map 8888 -> 80, in which case a LAN client must use port 80. The
 * whichIP probe this replaced tried exactly that order — public+bPort, then LAN:80, then LAN:bPort
 * (AuthActions whichIP) — and dropping the bare LAN:80 candidate left a LAN client with NO reachable
 * URL for any site whose public port is not 80 (cs256 publishes 8888). #86bb7phhh
 *
 * @param {object} server - member server ({ sIP, sLocalIP, bPort, ... })
 * @param {string} vantageHost - the address this client reached the controller on
 * @returns {string[]} ordered, de-duplicated `http://<ip>[:port]/JSON/` candidates
 */
export const buildOrderedMemberUrls = (server, vantageHost) => {
  if (!server) return [];
  const port = server.bPort;
  const portSuffix = port && port !== 80 ? `:${port}` : '';
  const ips = orderCandidateIps(server, vantageHost);
  const chosen = ips.length ? ips : [server.sIP || server.sLocalIP].filter(Boolean);
  const urls = [];
  chosen.forEach((ip) => {
    // The extra bare-:80 candidate only makes sense for a genuinely PRIVATE LAN address that differs
    // from the public one — that is the NAT-mapped case (public 8888 -> internal 80). When sLocalIP is
    // public, or equal to sIP, bPort is the only port that can be right.
    const isNattedLan = ip === server.sLocalIP && ip !== server.sIP && isPrivateHost(ip);
    if (isNattedLan && portSuffix) urls.push(`http://${ip}/JSON/`);
    urls.push(`http://${ip}${portSuffix}/JSON/`);
  });
  // De-dup: sLocalIP === sIP, or bPort 80, collapses candidates onto one URL.
  return urls.filter((u, i) => urls.indexOf(u) === i);
};

/**
 * Centralized utility for getting server host and port information.
 *
 * These functions check for environment variable overrides first, then fall back
 * to window.location values. This is useful for Docker development environments
 * where the app runs on localhost:3000 but needs to connect to a different backend.
 *
 * Environment Variables:
 * - REACT_APP_SERVER_HOST: Override hostname (e.g., "192.168.0.22")
 * - REACT_APP_SERVER_PORT: Override port (e.g., "80")
 */

/**
 * Get the current hostname
 * @returns {string} Current hostname from env var or window.location
 */
export const getHost = () => {
  // Check for environment variable override first
  if (process.env.REACT_APP_SERVER_HOST) {
    return process.env.REACT_APP_SERVER_HOST;
  }

  // Fall back to window.location
  if (typeof window === 'undefined') return '';
  return window.location.hostname;
};

/**
 * Get the current port
 * @returns {string} Current port from env var or window.location, with protocol-appropriate defaults
 */
export const getPort = () => {
  // Check for environment variable override first
  if (process.env.REACT_APP_SERVER_PORT) {
    return process.env.REACT_APP_SERVER_PORT;
  }

  // Fall back to window.location
  if (typeof window === 'undefined') return '80';

  // If port is explicit in URL, use it
  if (window.location.port) {
    return window.location.port;
  }

  // Return protocol-appropriate default port
  // HTTPS uses 443, HTTP uses 80
  return window.location.protocol === 'https:' ? '443' : '80';
};

/**
 * Get both host and port together
 * @returns {{ host: string, port: string }} Object containing host and port
 */
export const getServerInfo = () => {
  return {
    host: getHost(),
    port: getPort()
  };
};

/**
 * Get the base URL (protocol + host + port)
 * @returns {string} Base URL like "http://192.168.1.100:80"
 */
export const getBaseUrl = () => {
  if (typeof window === 'undefined') return '';
  const protocol = window.location.protocol; // 'http:' or 'https:'
  const host = getHost();
  const port = getPort();

  // Don't include default ports in URL
  const defaultPort = protocol === 'https:' ? '443' : '80';
  const portSuffix = port === defaultPort ? '' : `:${port}`;

  return `${protocol}//${host}${portSuffix}`;
};

/**
 * Get WebSocket protocol (ws: or wss:)
 * @returns {string} WebSocket protocol based on current page protocol
 */
export const getWsProtocol = () => {
  if (typeof window === 'undefined') return 'ws:';
  return window.location.protocol === 'https:' ? 'wss:' : 'ws:';
};

/**
 * Build a WebSocket URL for camera streaming
 *
 * This is the primary function for building WebSocket URLs for camera streams.
 * It handles protocol/port matching correctly and supports session-based auth.
 *
 * Jump System Support:
 *   - After a "Jump System" action, WebSocket URLs point to the jumped server
 *   - This requires CORS headers on the target server
 *
 * @param {number} cameraId - Camera ID (1-N)
 * @param {number} profile - Stream profile (1=full, 2=lower quality)
 * @param {Object} auth - Authentication object
 * @param {string} [auth.session] - Session token (preferred)
 * @param {string} [auth.username] - Username (fallback)
 * @param {string} [auth.password] - Password (fallback)
 * @param {Object} [options] - Optional overrides
 * @param {string} [options.host] - Override host
 * @param {string} [options.port] - Override port
 * @returns {string} Complete WebSocket URL
 */
export const buildWebSocketUrl = (cameraId, profile, auth, options = {}) => {
  if (typeof window === 'undefined') {
    return '';
  }

  // Check for Jump System - if we've jumped to a different server, use that server
  const jumpedServerUrl = localStorage.getItem('jumpedServerUrl');
  if (jumpedServerUrl && !options.host) {
    // Parse jumped URL to extract host and port
    // jumpedServerUrl is like "http://192.168.0.143:80/JSON/"
    try {
      const url = new URL(jumpedServerUrl);
      const jumpedHost = url.hostname;
      const jumpedPort = url.port || (url.protocol === 'https:' ? '443' : '80');

      // Build auth query string
      let authQuery = '';
      if (auth.session) {
        authQuery = `sess=${encodeURIComponent(auth.session)}`;
      } else if (auth.username && auth.password) {
        authQuery = `user=${encodeURIComponent(auth.username)}&pass=${encodeURIComponent(auth.password)}`;
      }

      const wsProtocol = url.protocol === 'https:' ? 'wss' : 'ws';
      return `${wsProtocol}://${jumpedHost}:${jumpedPort}/ws/cam${cameraId}-pro${profile}?${authQuery}`;
    } catch (e) {
      debugWarn('[buildWebSocketUrl] Failed to parse jumped URL:', e);
      // Fall through to normal logic
    }
  }

  const isSecure = window.location.protocol === 'https:';
  const protocol = isSecure ? 'wss' : 'ws';

  // Build auth query string - prefer session-based auth
  let authQuery = '';
  if (auth.session) {
    authQuery = `sess=${encodeURIComponent(auth.session)}`;
  } else if (auth.username && auth.password) {
    // Fallback to user/pass (legacy support)
    authQuery = `user=${encodeURIComponent(auth.username)}&pass=${encodeURIComponent(auth.password)}`;
  }

  // Development mode: connect SAME-ORIGIN so setupProxy.js's '/ws' route forwards to
  // REACT_APP_SERVER_HOST. This mirrors getApiUrl()/buildWsUrl(), which already route
  // through the dev proxy — keeping dev faithful to production, where the NVR serves the
  // app same-origin. Without this, the base-login WS would open a DIRECT cross-origin
  // socket to the NVR IP (REACT_APP_SERVER_HOST is only meant to be the proxy TARGET,
  // not a URL input). Skipped for an explicit options.host (eView per-NVR streaming) and
  // after a Jump (handled above, which points at the jumped NVR by design).
  if (process.env.NODE_ENV === 'development' && !options.host) {
    return `${protocol}://${window.location.host}/ws/cam${cameraId}-pro${profile}?${authQuery}`;
  }

  // Use options, or getHost()/getPort() which respect REACT_APP_SERVER_* env vars
  const host = options.host || getHost();
  const port = options.port || getPort();

  return `${protocol}://${host}:${port}/ws/cam${cameraId}-pro${profile}?${authQuery}`;
};

/**
 * Build a WebSocket URL (legacy function - prefer buildWebSocketUrl for camera streams)
 *
 * Development mode: Uses current host (proxy forwards to backend)
 * Production mode: Uses REACT_APP_SERVER_HOST if set, otherwise current host
 *
 * @param {string} path - WebSocket path (e.g., '/ws/cam1-pro2')
 * @param {Object} params - Optional query parameters
 * @returns {string} Complete WebSocket URL
 */
export const buildWsUrl = (path, params = {}) => {
  const isDevelopment = process.env.NODE_ENV === 'development';
  const wsProtocol = getWsProtocol();

  // Build query string
  const queryString = Object.keys(params)
    .map(key => `${key}=${params[key]}`)
    .join('&');

  const query = queryString ? `?${queryString}` : '';

  // Development mode: Use current location (proxy forwards WebSocket connections)
  if (isDevelopment) {
    if (typeof window !== 'undefined') {
      const currentHost = window.location.hostname;
      const currentPort = window.location.port;
      return `${wsProtocol}//${currentHost}:${currentPort}${path}${query}`;
    }
    return `${wsProtocol}//localhost:3000${path}${query}`;
  }

  // Production mode: Use env vars if set, otherwise current location
  const host = getHost();
  const port = getPort();

  return `${wsProtocol}//${host}:${port}${path}${query}`;
};

/**
 * Get the API base URL for backend JSON-RPC calls
 *
 * Jump System Support:
 *   - After a "Jump System" action, calls go directly to the jumped server
 *   - The jumped server URL is stored in localStorage
 *   - This requires CORS headers on the target server
 *
 * Development mode (npm start):
 *   - Uses relative URL '/JSON/'
 *   - setupProxy.js forwards to REACT_APP_SERVER_HOST automatically
 *   - No CORS issues (same-origin from browser's perspective)
 *
 * Production mode (npm run build):
 *   - If REACT_APP_SERVER_HOST is set: Uses full URL
 *   - If NOT set: Uses relative URL (same domain)
 *
 * @returns {string} API URL
 */
export const getApiUrl = () => {
  // Check for Jump System - if we've jumped to a different server, use that URL directly
  if (typeof window !== 'undefined') {
    const jumpedServerUrl = localStorage.getItem('jumpedServerUrl');
    if (jumpedServerUrl) {
      // In dev mode, route through /server-check/ proxy to avoid CORS
      if (process.env.NODE_ENV === 'development') {
        try {
          const url = new URL(jumpedServerUrl);
          const hostPort = url.port ? `${url.hostname}:${url.port}` : url.hostname;
          return `/server-check/${hostPort}${url.pathname}`;
        } catch (e) {
          return jumpedServerUrl;
        }
      }
      return jumpedServerUrl;
    }
  }

  const isDevelopment = process.env.NODE_ENV === 'development';

  // Development mode: Always use relative URL (proxy handles forwarding)
  if (isDevelopment) {
    return '/JSON/';
  }

  // Production mode: Use full URL if env vars are set, otherwise relative
  if (process.env.REACT_APP_SERVER_HOST) {
    const protocol = typeof window !== 'undefined' && window.location.protocol === 'https:' ? 'https:' : 'http:';
    const host = getHost();
    const port = getPort();

    // Don't include default ports in URL
    const defaultPort = protocol === 'https:' ? '443' : '80';
    const portSuffix = port === defaultPort ? '' : `:${port}`;

    return `${protocol}//${host}${portSuffix}/JSON/`;
  }

  // Production without env vars: use relative URL (same domain as the app)
  return '/JSON/';
};

/**
 * Set the jumped server URL (called by Jump System)
 * @param {string|null} url - The server URL to use, or null to clear
 */
export const setJumpedServerUrl = (url) => {
  if (typeof window === 'undefined') return;

  if (url) {
    localStorage.setItem('jumpedServerUrl', url);
  } else {
    localStorage.removeItem('jumpedServerUrl');
  }
};

/**
 * Convert a direct NVR URL to a dev-proxy URL to avoid CORS.
 * In dev: http://host:port/path → /server-check/host:port/path
 * In prod: returns the URL unchanged.
 */
export const devProxyUrl = (directUrl) => {
  if (process.env.NODE_ENV !== 'development') return directUrl;
  try {
    const url = new URL(directUrl);
    const hostPort = url.port ? `${url.hostname}:${url.port}` : url.hostname;
    return `/server-check/${hostPort}${url.pathname}${url.search}`;
  } catch (e) {
    return directUrl;
  }
};

/**
 * Clear the jumped server URL (called on logout)
 */
export const clearJumpedServerUrl = () => {
  if (typeof window !== 'undefined') {
    localStorage.removeItem('jumpedServerUrl');
  }
};

/**
 * Build WebSocket URL for eView mode (multi-NVR streaming)
 *
 * In eView mode, cameras can come from different NVRs. Each NVR has its own:
 *   - Server URL (HTTP base URL)
 *   - Session token (sSess from login to that server)
 *
 * This function looks up the correct server and session for a camera based on its bSerial.
 *
 * @param {Object} camera - Camera object with bSerial, bID
 * @param {number} profile - Stream profile (1=full, 2=lower quality)
 * @param {Object} serverSessions - Map of { bSerial: { sSess, serverUrl, sName } }
 * @param {string} fallbackSession - Fallback session if camera's server not found
 * @returns {string} Complete WebSocket URL for this camera's NVR
 */
// Pick the auth query string for cross-NVR streaming.
//
// Auth priority (matches Java dview LiveStream.java:97-117):
//   1. Per-NVR session from serverSessions[bSerial].sSess (only populated
//      after an explicit login to that child NVR)
//   2. user=X&pass=Y URL credentials (works on any mpengine — webcam.c's
//      validate_session falls through to validate_login on session miss)
//   3. Controller's session as last resort (works on the controller, will
//      404 on child NVRs but harmless to try)
//
// fallbackAuth shape: { session, username, password } — passed in by callers
// that read these from redux state.auth.
const pickStreamAuthQuery = (perNvrSession, fallbackAuth) => {
  if (perNvrSession) return `sess=${encodeURIComponent(perNvrSession)}`;
  if (fallbackAuth && fallbackAuth.username && fallbackAuth.password) {
    return `user=${encodeURIComponent(fallbackAuth.username)}&pass=${encodeURIComponent(fallbackAuth.password)}`;
  }
  if (fallbackAuth && fallbackAuth.session) return `sess=${encodeURIComponent(fallbackAuth.session)}`;
  return '';
};

export const buildEviewWebSocketUrl = (camera, profile, serverSessions, fallbackAuth) => {
  if (typeof window === 'undefined' || !camera) {
    return '';
  }

  // Back-compat: accept a bare string fallback (the old fallbackSession signature)
  if (typeof fallbackAuth === 'string') {
    fallbackAuth = { session: fallbackAuth };
  }

  const { bSerial, bID } = camera;
  const cameraId = bID;

  // Look up this camera's server in serverSessions
  const serverInfo = serverSessions && serverSessions[bSerial];

  if (serverInfo && serverInfo.serverUrl) {
    // Parse the server URL to extract host, port, and protocol
    try {
      const url = new URL(serverInfo.serverUrl);
      const host = url.hostname;
      const port = url.port || (url.protocol === 'https:' ? '443' : '80');
      const wsProtocol = url.protocol === 'https:' ? 'wss' : 'ws';

      const authQuery = pickStreamAuthQuery(serverInfo.sSess, fallbackAuth);
      if (!authQuery) {
        debugWarn(`[buildEviewWebSocketUrl] No usable auth for NVR ${bSerial}, returning empty URL`);
        return '';
      }

      debugLog(`[buildEviewWebSocketUrl] Camera ${cameraId} (NVR ${bSerial}): ${host}:${port} auth=${authQuery.split('=')[0]}=...`);

      return `${wsProtocol}://${host}:${port}/ws/cam${cameraId}-pro${profile}?${authQuery}`;
    } catch (e) {
      debugWarn(`[buildEviewWebSocketUrl] Failed to parse serverUrl for NVR ${bSerial}:`, e);
    }
  }

  // Fallback: use current jumpedServerUrl or local server with whatever auth we have
  debugWarn(`[buildEviewWebSocketUrl] No serverInfo for NVR ${bSerial}, using local server URL`);
  return buildWebSocketUrl(cameraId, profile, {
    session: fallbackAuth?.session,
    username: fallbackAuth?.username,
    password: fallbackAuth?.password,
  });
};

/**
 * Build JPEG stream URL for eView mode (cross-NVR camera fetch).
 *
 * Mirrors buildEviewWebSocketUrl auth priority. Returns the full URL up to
 * (and including) the trailing `&ts=` — caller appends a cache-busting
 * timestamp. Returns null if camera is invalid or no auth is usable.
 *
 * @param {Object} camera - { bSerial, bID, ... }
 * @param {boolean} isHighRes - true → /mpe/cam{N}.jpg (full), false → /mpe/scam{N}.jpg (sub)
 * @param {Object} serverSessions - map of { bSerial: { sSess, serverUrl, ... } }
 * @param {Object} fallbackAuth - { session, username, password } from state.auth
 * @returns {string|null}
 */
export const buildEviewJpegUrl = (camera, isHighRes, serverSessions, fallbackAuth) => {
  if (!camera || !camera.bSerial) {
    return null;
  }

  // Back-compat: callers used to pass `(camera, sSess, isHighRes, serverSessions)`.
  // Detect that shape and translate.
  if (typeof isHighRes === 'string' || typeof isHighRes === 'undefined') {
    // Old shape: buildEviewJpegUrl(camera, sSess, isHighRes, serverSessions)
    // Don't try to be clever — the new callsites use the new shape. If this
    // hits, log and bail so we catch it loudly.
    debugWarn('[buildEviewJpegUrl] called with legacy (camera, sSess, isHighRes, serverSessions) signature; expected (camera, isHighRes, serverSessions, fallbackAuth)');
    return null;
  }

  const serverInfo = serverSessions && serverSessions[camera.bSerial];
  if (!serverInfo || !serverInfo.serverUrl) {
    return null;
  }

  const authQuery = pickStreamAuthQuery(serverInfo.sSess, fallbackAuth);
  if (!authQuery) {
    debugWarn(`[buildEviewJpegUrl] No usable auth for NVR ${camera.bSerial}`);
    return null;
  }

  // Extract base URL (strip /JSON/ suffix if present)
  const baseUrl = serverInfo.serverUrl.replace(/\/JSON\/?$/, '');
  const endpoint = isHighRes ? `/mpe/cam${camera.bID}.jpg` : `/mpe/scam${camera.bID}.jpg`;
  return `${baseUrl}${endpoint}?${authQuery}&ts=`;
};

/**
 * Build a complete API URL with path
 *
 * Jump System Support:
 *   - After a "Jump System" action, URLs are built for the jumped server
 *   - This requires CORS headers on the target server
 *
 * Development mode: Uses relative URL (proxy forwards)
 * Production mode: Uses full URL if env vars set, otherwise relative
 *
 * @param {string} path - API path (e.g., '/mpe/cam1.jpg')
 * @returns {string} Complete URL
 */
export const buildApiUrl = (path) => {
  // Ensure path starts with /
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;

  // Check for Jump System - if we've jumped to a different server, build URL for that server
  if (typeof window !== 'undefined') {
    const jumpedServerUrl = localStorage.getItem('jumpedServerUrl');
    if (jumpedServerUrl) {
      // jumpedServerUrl is like "http://192.168.0.143:80/JSON/"
      // In dev mode, route through /server-check/ proxy to avoid CORS
      if (process.env.NODE_ENV === 'development') {
        try {
          const url = new URL(jumpedServerUrl);
          const hostPort = url.port ? `${url.hostname}:${url.port}` : url.hostname;
          return `/server-check/${hostPort}${normalizedPath}`;
        } catch (e) {
          // Fall through
        }
      }
      // Extract base URL (everything before /JSON/)
      const baseUrl = jumpedServerUrl.replace(/\/JSON\/?$/, '');
      return `${baseUrl}${normalizedPath}`;
    }
  }

  const isDevelopment = process.env.NODE_ENV === 'development';

  // Development mode: Use relative URL (proxy handles forwarding)
  if (isDevelopment) {
    return normalizedPath;
  }

  // Production mode: Use full URL if env vars are set
  if (process.env.REACT_APP_SERVER_HOST) {
    const protocol = typeof window !== 'undefined' && window.location.protocol === 'https:' ? 'https:' : 'http:';
    const host = getHost();
    const port = getPort();

    const defaultPort = protocol === 'https:' ? '443' : '80';
    const portSuffix = port === defaultPort ? '' : `:${port}`;

    return `${protocol}//${host}${portSuffix}${normalizedPath}`;
  }

  // Production without env vars: use relative URL
  return normalizedPath;
};
